'use client';

import { Select } from '@base-ui/react/select';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { type CSSProperties, useCallback } from 'react';
import { MdCheck } from 'react-icons/md';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { useRipplePress } from '../useRipplePress';
import type { SortOption, sortByStoreProps } from './types';

export type { SortOption, sortByStoreProps };

type Props<TSortValue extends string, TFilterOptions extends string> = {
  isStickyBar?: boolean;
  scrollBackToMainBar?: () => void;
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: sortByStoreProps<TSortValue, TFilterOptions>;
  sortByLabel?: string;
  filterKey: TFilterOptions;
  sortBySelectEvent?: (
    overrideProps?: Record<string, string | object> | null,
    callback?: (() => void) | null,
  ) => undefined;
};

const triggerClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: '2',
  rowGap: '2',
  height: '10',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderBottomColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
  backgroundColor: 'white',
  fontFamily: '[inherit]',
  cursor: 'pointer',
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'hidden',
  _focus: {
    outlineStyle: 'none',
  },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
  _after: {
    content: '""',
    display: 'block',
    position: 'absolute',
    width: 'full',
    height: 'full',
    top: '0',
    left: '0',
    pointerEvents: 'none',
    backgroundImage: '[radial-gradient(circle,token(colors.green.main) 10%,transparent 10.01%)]',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '[50%]',
    backgroundPositionY: '[50%]',
    // No resting `transition` here: the async Panda stylesheet applies these
    // properties over the already-painted (inline critical CSS) trigger, and a
    // resting transition would animate that change into a green ripple on load.
    // The ripple is driven by the `ripple` keyframe, which useRipplePress runs
    // by toggling `data-rippling` on pointer/keyboard activation.
    transform: '[scale(0)]',
    opacity: '0',
  },
  '&[data-rippling]::after': {
    animationName: 'ripple',
    animationDuration: '[0.4s]',
    animationTimingFunction: 'ease-out',
  },
});

const triggerActiveClass = css({
  backgroundColor: 'green.light',
  borderTopWidth: 'medium',
  borderRightWidth: 'medium',
  borderBottomWidth: 'medium',
  borderLeftWidth: 'medium',
  borderTopColor: 'green.main',
  borderRightColor: 'green.main',
  borderBottomColor: 'green.main',
  borderLeftColor: 'green.main',
});

const triggerLabelClass = css({
  textStyle: 'filterItem',
  color: 'var(--label-color)',
});

const triggerLabelActiveClass = css({ color: 'var(--label-color)' });

const popupClass = css({
  backgroundColor: 'neutral.white',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
  borderBottomRightRadius: '3',
  borderBottomLeftRadius: '3',
  boxShadow: '[0 4px 12px rgba(0,0,0,0.15)]',
  paddingBlockStart: '1',
  paddingBlockEnd: '1',
  outlineStyle: 'none',
});

const popupHeaderClass = css({
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  height: '9',
  textStyle: 'filterItem',
  color: 'neutral.dark',
  display: 'flex',
  alignItems: 'center',
});

const itemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  height: '9',
  width: 'full',
  cursor: 'pointer',
  fontSize: 'sm',
  color: 'neutral.dark',
  fontWeight: 'normal',
  outlineStyle: 'none',
  '&[data-highlighted]': { backgroundColor: 'neutral.lightest' },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
});

const itemActiveClass = css({
  backgroundColor: 'neutral.white',
  '&[data-highlighted]': { backgroundColor: 'neutral.white' },
  '&[data-highlighted]:hover': { backgroundColor: 'neutral.lightest' },
});

const itemIndicatorWrapperClass = css({
  display: 'flex',
  width: '6',
  minWidth: '6',
  justifyContent: 'flex-end',
  flexShrink: '0',
});

const itemIndicatorClass = css({
  color: 'green.main',
});

export const SortByDropdown = <TSortValue extends string, TFilterOptions extends string>({
  isStickyBar,
  scrollBackToMainBar,
  sortOptions,
  useStore,
  sortByLabel = 'Sort By',
  filterKey,
  sortBySelectEvent,
}: Props<TSortValue, TFilterOptions>) => {
  const barKey = isStickyBar ? 'stickyBar' : 'normalBar';
  const { sortBy, handleOpenedFilters, handleSortBy } = useStore(
    ({ sortBy, handleOpenedFilters, handleSortBy }) => ({
      sortBy,
      handleOpenedFilters,
      handleSortBy,
    }),
    shallow,
  );
  const isOpen = useStore(state => state.openedFilters[barKey]?.[filterKey] ?? false, shallow);
  const isSelected = !!sortBy;
  const { ref: rippleRef } = useRipplePress();

  const displayLabel = sortOptions.find(o => o.value === sortBy)?.label ?? sortByLabel;

  const handleValueChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      handleOpenedFilters(filterKey, barKey, false);

      const selectedOption = sortOptions.find(o => o.value === value);
      if (sortBySelectEvent && selectedOption) {
        sortBySelectEvent({ additional_attributes: { sort_by_value: selectedOption.label } });
      }

      setTimeout(() => {
        handleSortBy(value as TSortValue);
        if (scrollBackToMainBar) scrollBackToMainBar();
      }, 0);
    },
    [
      handleSortBy,
      sortOptions,
      sortBySelectEvent,
      handleOpenedFilters,
      filterKey,
      barKey,
      scrollBackToMainBar,
    ],
  );

  return (
    <Select.Root
      value={sortBy ?? ''}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={open => handleOpenedFilters(filterKey, barKey, open)}
      modal={false}
    >
      <Select.Trigger
        ref={rippleRef}
        id="sort-by-dropdown-button"
        aria-haspopup="listbox"
        className={cx(triggerClass, isSelected && triggerActiveClass)}
      >
        <span
          className={cx(triggerLabelClass, isSelected && triggerLabelActiveClass)}
          style={
            {
              '--label-color': isSelected ? '#008254' : '#64666A',
            } as CSSProperties
          }
        >
          {displayLabel}
        </span>
        <Select.Icon>
          <ExpandIcon width={15} color={isSelected ? '#008254' : '#64666A'} />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Positioner
          sideOffset={8}
          align="start"
          alignItemWithTrigger={false}
          style={{ zIndex: 200 }}
        >
          <Select.Popup className={popupClass}>
            <div className={popupHeaderClass}>{sortByLabel}</div>
            <Select.List>
              {sortOptions.map(({ label, value }) => (
                <Select.Item
                  key={value}
                  value={value}
                  className={cx(itemClass, isSelected && itemActiveClass)}
                >
                  <Select.ItemText className={cx(isSelected && css({ fontWeight: 'normal' }))}>
                    {label}
                  </Select.ItemText>
                  <span className={itemIndicatorWrapperClass}>
                    <Select.ItemIndicator className={itemIndicatorClass}>
                      <MdCheck size={16} />
                    </Select.ItemIndicator>
                  </span>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
};
